import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ThemeContext from '@/components/ThemeContext/ThemeContext';

//Types
import { ThemeContextType } from '@/@types/context/ThemeContext';
import { ChartProps } from '@/@types/ui/Charts';

export default (WrappedComponent: React.Component<ChartProps> | React.FunctionComponent<ChartProps>) => (props: ChartProps) => {
  const { handleClick, series, labels, categories, options, title, height, width } = props;
  const themeContext = useContext(ThemeContext) as ThemeContextType;

  const [theme, setTheme] = useState(themeContext.theme);
  const [chartOptions, setChartOptions] = useState<ApexCharts.ApexOptions | null>(null);
  const [ctx, setCtx] = useState(null);

  const { t } = useTranslation();

  const is = (d: any) => d !== null && d !== undefined && Array.isArray(d) && d.length;

  const mergeOpts = (inOpts: ApexCharts.ApexOptions, opts: ApexCharts.ApexOptions) => {
    if (!opts) return inOpts;
    for (let key in opts) {
      if (inOpts.hasOwnProperty(key)) {
        //@ts-ignore
        if (opts.hasOwnProperty(key) && (typeof opts[key] !== 'object' || Array.isArray(opts[key]))) {
          //@ts-ignore
          inOpts[key] = opts[key];
        } else {
          //@ts-ignore
          inOpts[key] = mergeOpts(inOpts[key], opts[key]);
        }
      } else {
        //@ts-ignore
        inOpts[key] = opts[key];
      }
    }
    return inOpts;
  };

  useEffect(() => {
    if (is(series) && (is(labels) || is(categories))) {
      const initialOpts: ApexCharts.ApexOptions = {
        stroke: { show: false, curve: 'smooth' },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                total: {
                  show: true,
                  label: t('statistics.total'),
                  fontSize: '28px',
                },
                name: {
                  formatter: (v: string) => t(v),
                },
              },
            },
            expandOnClick: handleClick !== undefined ? false : true,
          },
          bar: {
            horizontal: true,
          },
        },
        chart: {
          events: {
            dataPointSelection: handleClick,
            beforeMount: (c: any) => setCtx(c),
          },
          animations: {
            enabled: true,
            easing: 'easeinout',
            speed: 1000,
            animateGradually: {
              enabled: true,
              delay: 150,
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350,
            },
          },
        },
        dataLabels: {
          style: {
            fontSize: '14px',
          },
          formatter: (val: string | number) => (val === 0 ? '' : val),
        },
        legend: {
          formatter: (v: string) => t(v),
          fontSize: '18px',
        },
        tooltip: {
          y: {
            title: {
              formatter: (v: string) => t(v),
            },
          },
          fillSeriesColor: false,
        },
        title: {
          text: t(title),
          style: {
            fontSize: '18px',
          },
        },
        labels: labels || [],
        xaxis: {
          categories: categories || [],
          labels: {
            formatter: (v: string) => t(v),
            style: { fontSize: '14px' },
          },
        },
        yaxis: [
          {
            showAlways: false,
            showForNullSeries: false,
            labels: {
              formatter: (v: string | number) => t(typeof v === 'number' ? v.toString() : v),
              style: { fontSize: '13px' },
            },
          },
        ],
      };

      setChartOptions(mergeOpts(initialOpts, options));
      // setChartOptions(initialOpts);
    }
  }, [series, labels, categories]);

  useEffect(() => {
    if (ctx) {
      //@ts-ignore
      ctx.updateOptions(
        {
          theme: {
            palette: 'palette7',
            mode: themeContext.theme,
          },
        },
        true,
        true
      );
    }
    setTheme(themeContext.theme);
  }, [themeContext.theme, ctx]);

  return chartOptions !== null ? (
    //@ts-ignore
    <WrappedComponent height={height} width={width} options={chartOptions} series={series} />
  ) : null;
};
